// Crop-back cutter (docs: photohawk-serverless docs/preview-first-uploads.md, "Crop-back"). Pure and
// environment-neutral like resizeCore — runs in the resize Web Worker and as the main-thread fallback.
//
// The server detected faces / people on the small preview copy and asks for each one cut from the
// ORIGINAL: a rect as fractions of the photo (upright), a long-edge cap and a JPEG quality. The geometry
// is the server's — this module only supplies pixels — and must match lib cropBack.cropGeometry exactly
// (floor/ceil of the rect in the original's pixels, ignoring float noise; long edge capped, never
// upscaled), because the server checks each crop's aspect and size against it.

import { decodeDrawable, decodeScaled, pickScale, readJpegHeader } from "./scaledDecode";
import { drawUpright, uprightSize, type Drawable } from "./exifOrientation";

// An intermediate upright canvas (only for a decode whose EXIF orientation is still to apply) stays under
// Safari's canvas area limit (16.7 MP on iOS): a 24 MP original is drawn at ~0.8x for it.
const MAX_UPRIGHT_PIXELS = 16_000_000;

export type Rect = [number, number, number, number];

export interface CropRequest {
  rect: Rect;
  /** Long-edge cap of the output, px. */
  maxEdge: number;
  /** JPEG quality, 0..1. */
  quality: number;
}

export interface CutCrop {
  blob: Blob;
  width: number;
  height: number;
}

export interface CropGeometry {
  left: number;
  top: number;
  width: number;
  height: number;
  outW: number;
  outH: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const floorE = (v: number) => Math.floor(v + 1e-6);
const ceilE = (v: number) => Math.ceil(v - 1e-6);

/** Server lib cropBack.cropGeometry, verbatim. */
export function cropGeometry(rect: Rect, W: number, H: number, maxEdge: number): CropGeometry {
  const left = clamp(floorE(rect[0] * W), 0, W - 1);
  const top = clamp(floorE(rect[1] * H), 0, H - 1);
  const right = clamp(ceilE((rect[0] + rect[2]) * W), left + 1, W);
  const bottom = clamp(ceilE((rect[1] + rect[3]) * H), top + 1, H);
  const width = right - left;
  const height = bottom - top;
  const s = Math.min(1, maxEdge / Math.max(width, height));
  return { left, top, width, height, outW: Math.max(1, Math.round(width * s)), outH: Math.max(1, Math.round(height * s)) };
}

/** The fraction of the original's resolution the crops need (the most demanding one), 0..1. */
export function neededFraction(items: CropRequest[], W: number, H: number): number {
  let f = 0;
  for (const it of items) {
    const g = cropGeometry(it.rect, W, H, it.maxEdge);
    f = Math.max(f, Math.max(g.outW, g.outH) / Math.max(g.width, g.height));
  }
  return Math.min(1, f);
}

/**
 * Cut the requested crops from an original JPEG. Returns them in order, or null when the file can't be
 * used (not a JPEG, won't decode by any route) — the caller then tells the server to fall back to its
 * own crops. Crops always follow cropGeometry in the ORIGINAL's pixels, whatever size it decoded at.
 */
export async function cutCrops(file: Blob, items: CropRequest[]): Promise<CutCrop[] | null> {
  if (!items.length || typeof OffscreenCanvas === "undefined" || typeof createImageBitmap === "undefined") return null;
  const header = await readJpegHeader(file);
  if (!header) return null;
  const { width: W, height: H } = header;
  let img: Drawable | null = null;
  try {
    const scale = pickScale(neededFraction(items, W, H), header);
    // decodeDrawable only returns an image whose upright frame agrees with the header (or every rect would
    // be off): upright, or one whose EXIF orientation is applied below.
    img = await decodeDrawable(file, header, scale);
    // Last resort, when this browser can't decode the original in full: a 1/2 decode. A 20MP original at
    // 1/2 still has ~2.3x the resolution of the 1200px copy the server cut its own crops from (and the
    // server keeps its own crop for any that gain nothing). Smaller decodes are barely above the copy.
    if (!img && scale < 2) {
      const half = await decodeScaled(file, header, 2);
      if (half) img = { source: half, width: half.width, height: half.height, orientation: 1, close: () => half.close() };
    }
    if (!img) return null;
    // Crops are rects of the UPRIGHT photo: a decode still needing its orientation is drawn upright first.
    let source: CanvasImageSource = img.source;
    let size = uprightSize(img);
    if (img.orientation !== 1) {
      const k = Math.min(1, Math.sqrt(MAX_UPRIGHT_PIXELS / (size.width * size.height)));
      const uw = Math.max(1, Math.round(size.width * k));
      const uh = Math.max(1, Math.round(size.height * k));
      const upright = new OffscreenCanvas(uw, uh);
      const uctx = upright.getContext("2d");
      if (!uctx) return null;
      drawUpright(uctx, img, uw, uh);
      source = upright as unknown as CanvasImageSource;
      size = { width: uw, height: uh };
    }
    const kx = size.width / W;
    const ky = size.height / H;
    const out: CutCrop[] = [];
    for (const it of items) {
      const g = cropGeometry(it.rect, W, H, it.maxEdge);
      const canvas = new OffscreenCanvas(g.outW, g.outH);
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(source, g.left * kx, g.top * ky, g.width * kx, g.height * ky, 0, 0, g.outW, g.outH);
      const blob = await canvas.convertToBlob({ type: "image/jpeg", quality: it.quality });
      if (blob.type !== "image/jpeg") return null; // no JPEG encoder here — the server keeps its own crops
      out.push({ blob, width: g.outW, height: g.outH });
    }
    return out;
  } catch {
    return null;
  } finally {
    img?.close();
  }
}
